import { useEffect, useState } from "react";
import { Sparkles, Phone, MessageSquare, Mail, CalendarClock, Bell, StickyNote, Flame, AlertTriangle, Clock, TrendingUp, ZapOff, Users, CheckCircle2, Send } from "lucide-react";
import api from "../../lib/api";
import { eur } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { ScopeSwitcher } from "../common/ScopeSwitcher";
import ActionLogDialog from "../actions/ActionLogDialog";

const TYPE_VENTE = [
  { v: "all", l: "Toutes ventes" },
  { v: "Projet", l: "Projets" },
  { v: "Vente en direct", l: "Direct" },
];
const CATS = [
  { key: "all", label: "Toutes", icon: Sparkles },
  { key: "fortement_en_retard", label: "Fortement en retard", icon: AlertTriangle },
  { key: "a_relancer", label: "À relancer", icon: Bell },
  { key: "sans_reponse", label: "Sans réponse", icon: Clock },
  { key: "fort_potentiel", label: "Fort potentiel", icon: TrendingUp },
  { key: "activite_insuffisante", label: "Activité insuffisante", icon: ZapOff },
  { key: "client_a_surveiller", label: "Clients à surveiller", icon: Users },
];
const PRIO = {
  HAUTE: "bg-red-500/10 text-red-400 border-red-500/20",
  MOYENNE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  BASSE: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};
// mapping code recommandé -> type d'action du journal
const CODE_TO_TYPE = { appeler: "appel", envoyer_sms: "sms", envoyer_email: "email", programmer_rdv: "rdv", relancer_plus_tard: "relance", ne_pas_relancer: "note", cloturer_perdre: "note" };
const QUICK = [
  { type: "appel", label: "Appeler", icon: Phone },
  { type: "sms", label: "SMS", icon: MessageSquare },
  { type: "email", label: "Email", icon: Mail },
  { type: "rdv", label: "RDV", icon: CalendarClock },
  { type: "relance", label: "Relance", icon: Bell },
  { type: "note", label: "Note", icon: StickyNote },
];

export default function AssistantView() {
  const { isAdmin } = useViewRole();
  const [scope, setScope] = useState("real");
  const [typeVente, setTypeVente] = useState("all");
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [actionOpen, setActionOpen] = useState(false);
  const [actionCtx, setActionCtx] = useState(null);
  const [question, setQuestion] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askResult, setAskResult] = useState(null);

  const load = () => api.get(`/assistant/recommendations?scope=${scope}&type_vente=${encodeURIComponent(typeVente)}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("crm:data-changed", load);
    return () => window.removeEventListener("crm:data-changed", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, typeVente]);

  const ask = async (preset = null) => {
    const q = (preset || question).trim();
    if (!q) return;
    setAskLoading(true);
    try {
      const r = await api.get(`/assistant/ask?scope=${scope}&q=${encodeURIComponent(q)}`);
      setAskResult(r.data);
      if (!preset) setQuestion("");
    } catch (e) {
      setAskResult({ answer: "Je n'arrive pas à analyser cette question pour le moment.", items: [], meta: {} });
    } finally {
      setAskLoading(false);
    }
  };

  const logAction = (rec, presetType, recoCode) => {
    setActionCtx({
      client_id: rec.client_id, project_id: rec.project_id, devis_id: rec.dossier_id,
      client_nom: rec.client_nom, preset_type: presetType,
      reco_categorie: rec.categories[0] || "", reco_action_code: recoCode || rec.action_recommandee.code,
    });
    setActionOpen(true);
  };

  if (!data) return <div className="text-slate-500">Analyse des dossiers…</div>;

  const recs = cat === "all" ? data.recommendations : data.recommendations.filter((r) => r.categories.includes(cat));

  return (
    <div className="space-y-6" data-testid="assistant-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-400" />
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Assistant commercial</h1>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">IA</span>
          </div>
          <p className="text-slate-400 text-sm mt-1">Que dois-je faire aujourd'hui et pourquoi ? — recommandations explicables issues de vos données.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && <ScopeSwitcher scope={scope} setScope={setScope} />}
          <div className="flex gap-1 p-1 rounded-lg bg-[#111827] border border-[#1E293B]">
            {TYPE_VENTE.map((o) => (
              <button key={o.v} data-testid={`assistant-tv-${o.v}`} onClick={() => setTypeVente(o.v)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${typeVente === o.v ? "bg-amber-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-200"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Assistant conversationnel V2 */}
      <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-4 sm:p-5" data-testid="assistant-ask">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <div>
            <div className="font-semibold">Demandez à l'assistant</div>
            <div className="text-xs text-slate-500">Analyse commerciale + feuille de route issue des données CRM.</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="assistant-question-input" value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
            placeholder="Ex. Que dois-je faire aujourd'hui ?"
            className="flex-1 rounded-lg bg-[#0B0F17] border border-[#1E293B] px-3 py-2.5 text-sm text-slate-200 outline-none focus:border-amber-500" />
          <button data-testid="assistant-question-submit" onClick={() => ask()}
            disabled={askLoading || !question.trim()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-[#0B0F17] text-sm font-bold disabled:opacity-40">
            <Send className="h-4 w-4" /> {askLoading ? "Analyse…" : "Analyser"}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {["Que dois-je faire aujourd'hui ?", "Quels sont les dossiers sans réponse ?", "Montre-moi les forts potentiels", "Qui dois-je relancer ?"].map((q) => (
            <button key={q} onClick={() => ask(q)}
              className="px-2.5 py-1.5 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-xs text-slate-400 hover:text-slate-200">
              {q}
            </button>
          ))}
        </div>

        {askResult && (
          <div className="mt-4 rounded-lg bg-[#0B0F17] border border-[#1E293B] p-4" data-testid="assistant-answer">
            <div className="text-sm text-slate-200 font-medium">{askResult.answer}</div>

            {askResult.intent === "plan_du_jour" && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-red-500/10 text-red-300 border border-red-500/20">{askResult.meta?.haute || 0} haute(s)</span>
                <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/20">{askResult.meta?.moyenne || 0} moyenne(s)</span>
                <span className="px-2 py-1 rounded-md bg-slate-500/10 text-slate-300 border border-slate-500/20">{eur(askResult.meta?.valeur_selection || 0)} sélectionnés</span>
              </div>
            )}

            {askResult.items?.length > 0 && (
              <div className="mt-4 space-y-3">
                {askResult.items.map((x, i) => (
                  <div key={`${x.dossier_id}-${i}`} className="rounded-lg border border-[#1E293B] p-3">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{x.priorite === "HAUTE" ? "🔥 " : ""}{x.client_nom || "Client sans nom"}</div>
                        <div className="text-xs text-slate-500">{x.reference || "—"} · {x.famille || "—"} · {eur(x.montant || 0)}</div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 ${PRIO[x.priorite] || PRIO.BASSE}`}>{x.priorite}</span>
                    </div>

                    <div className="mt-2 text-xs text-slate-400">{x.situation || "Situation à analyser"}</div>

                    {x.raisons?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pourquoi</div>
                        <ul className="space-y-1">
                          {x.raisons.map((reason, idx) => (
                            <li key={idx} className="text-xs text-slate-300 flex items-start gap-1.5"><span className="text-amber-400">•</span>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-2 text-xs text-amber-300"><span className="font-semibold">Conseil : </span>{x.conseil}</div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {x.action_code === "appeler" && (
                        <button onClick={() => logAction(x, "appel", x.action_code)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-[#0B0F17] border border-amber-500">
                          <Phone className="h-3.5 w-3.5" /> Appeler
                        </button>
                      )}
                      {x.action_code === "envoyer_email" && (
                        <button onClick={() => logAction(x, "email", x.action_code)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-[#0B0F17] border border-amber-500">
                          <Mail className="h-3.5 w-3.5" /> Email
                        </button>
                      )}
                      {x.action_code === "programmer_rdv" && (
                        <button onClick={() => logAction(x, "rdv", x.action_code)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-[#0B0F17] border border-amber-500">
                          <CalendarClock className="h-3.5 w-3.5" /> RDV
                        </button>
                      )}
                      <button onClick={() => logAction(x, CODE_TO_TYPE[x.action_code] || "note", x.action_code)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#111827] text-slate-300 border border-[#1E293B]">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Journaliser
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Résumé priorités */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="assistant-summary">
        <SummaryCard label="À traiter" value={data.total} color="#38BDF8" testId="assistant-total" />
        <SummaryCard label="Priorité HAUTE" value={data.par_priorite.HAUTE} color="#EF4444" testId="assistant-prio-haute" />
        <SummaryCard label="Priorité MOYENNE" value={data.par_priorite.MOYENNE} color="#F59E0B" testId="assistant-prio-moyenne" />
        <SummaryCard label="Priorité BASSE" value={data.par_priorite.BASSE} color="#94A3B8" testId="assistant-prio-basse" />
      </div>

      {/* Filtres catégories */}
      <div className="flex flex-wrap gap-2">
        {CATS.map((c) => {
          const Icon = c.icon;
          const count = c.key === "all" ? data.total : data.par_categorie_counts[c.key];
          return (
            <button key={c.key} data-testid={`assistant-cat-${c.key}`} onClick={() => setCat(c.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${cat === c.key ? "bg-amber-500 text-[#0B0F17] border-amber-500" : "bg-[#111827] text-slate-300 border-[#1E293B] hover:border-slate-600"}`}>
              <Icon className="h-3.5 w-3.5" /> {c.label} <span className="opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Recommandations */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {recs.length === 0 && (
          <div className="col-span-full rounded-xl bg-[#111827] border border-[#1E293B] p-8 text-center" data-testid="assistant-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-slate-300 font-semibold">Aucune recommandation pertinente ici.</p>
            <p className="text-slate-500 text-sm mt-1">Tout est à jour pour ce filtre.</p>
          </div>
        )}
        {recs.map((r) => (
          <div key={r.dossier_id} className="rounded-xl bg-[#111827] border border-[#1E293B] p-5" data-testid={`assistant-rec-${r.dossier_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {r.priorite === "HAUTE" && <Flame className="h-4 w-4 text-red-400 shrink-0" />}{r.client_nom}
                </div>
                <div className="text-xs text-slate-500 font-mono-metric truncate">{r.reference_interne || r.numero} · {r.famille || "—"} · {r.type_vente}</div>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${PRIO[r.priorite]}`} data-testid={`assistant-prio-badge-${r.dossier_id}`}>{r.priorite}</span>
                <div className="font-mono-metric text-sm font-semibold mt-1">{eur(r.montant)}</div>
              </div>
            </div>

            <div className="mt-2 text-xs text-slate-400">{r.situation}</div>

            <div className="mt-3 rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Pourquoi c'est prioritaire</div>
              <ul className="space-y-1" data-testid={`assistant-raisons-${r.dossier_id}`}>
                {r.raisons.map((x, i) => (
                  <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5"><span className="text-amber-400 mt-0.5">•</span>{x}</li>
                ))}
              </ul>
            </div>

            <div className="mt-3 flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-amber-400 shrink-0" />
              <span className="text-slate-300">Action recommandée :</span>
              <span className="font-semibold text-amber-400" data-testid={`assistant-reco-${r.dossier_id}`}>{r.action_recommandee.label}</span>
            </div>
            {r.timing && (
              <div className="mt-1 text-[11px] text-slate-500">Échéance : {r.timing.date}{r.timing.estimee ? " (estimée +15j)" : r.timing.source === "journal" ? " (journal)" : ""}</div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {QUICK.map((q) => {
                const Icon = q.icon;
                const isReco = CODE_TO_TYPE[r.action_recommandee.code] === q.type;
                return (
                  <button key={q.type} data-testid={`assistant-rec-${r.dossier_id}-do-${q.type}`}
                    onClick={() => logAction(r, q.type, r.action_recommandee.code)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${isReco ? "bg-amber-500 text-[#0B0F17] border-amber-500" : "bg-[#0B0F17] text-slate-300 border-[#1E293B] hover:border-slate-600"}`}>
                    <Icon className="h-3.5 w-3.5" /> {q.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <ActionLogDialog open={actionOpen} onOpenChange={setActionOpen} context={actionCtx} onSaved={load} />
    </div>
  );
}

function SummaryCard({ label, value, color, testId }) {
  return (
    <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 font-mono-metric text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
